import type {
  DomainPropertySummary,
  DomainPropertyIR,
  ManifestEnumRuleInput,
} from "@granular-software/metamodel-core";
import { defineMetamodelPackage } from "@granular-software/metamodel-core";

function renderInlineStringUnion(values: string[]) {
  return values.map((value) => JSON.stringify(value)).join(" | ");
}

function normalizeEnumInput(enumSpec?: string[] | ManifestEnumRuleInput) {
  if (!enumSpec) return null;
  const config = Array.isArray(enumSpec) ? { values: enumSpec } : enumSpec;
  const values = (config.values || []).filter(
    (value): value is string => typeof value === "string" && value.length > 0,
  );
  if (values.length === 0) return null;
  const labels =
    Array.isArray(config.labels) && config.labels.length === values.length
      ? config.labels
      : values;
  return {
    values,
    labels,
    ...(config.message ? { message: config.message } : {}),
  };
}

export function buildEnumFieldMutations(
  fieldPath: string,
  enumSpec?: string[] | ManifestEnumRuleInput,
) {
  const normalized = normalizeEnumInput(enumSpec);
  if (!normalized) return [];

  const messageArg = normalized.message
    ? `, message: ${JSON.stringify(normalized.message)}`
    : "";
  const labelsArg = normalized.labels
    ? `, labels: ${JSON.stringify(normalized.labels)}`
    : "";

  return [
    {
      label: `set enum on ${fieldPath}`,
      query: `mutation { at(path: ${JSON.stringify(fieldPath)}) { set_enum(values: ${JSON.stringify(
        normalized.values,
      )}${labelsArg}${messageArg}) { values labels } } }`,
    },
  ];
}

export const enumMetamodelPackage = defineMetamodelPackage({
  id: "enum",
  docs: {
    fieldRows: [
      {
        key: "enum",
        description:
          'Allowed values. Use `{ "values": [...], "labels": [...] }` for authored display labels; otherwise each unchanged value is its display fallback.',
      },
    ],
  },
  graphql: {
    typeDefs: [
      `
      type EnumMetamodel {
        model: Model!
        values: [String!]!
        labels: [String!]!
        message: String
      }

      extend type Model {
        enum_rule: EnumMetamodel
      }

      extend type ModelMutation {
        set_enum(values: [String!]!, labels: [String!], message: String): EnumMetamodel
      }
      `,
    ],
    createResolvers({ run }) {
      return {
        EnumMetamodel: {
          model: (value) => value.model,
          values: (value) => value.values,
          labels: (value) => value.labels || [],
          message: (value) => value.message || null,
        },
        Model: {
          enum_rule: async (ant) => await run(ant.enum_rule()),
        },
        ModelMutation: {
          set_enum: async (ant, { values, labels, message }) => {
            const resolvedLabels =
              Array.isArray(labels) && labels.length === values.length
                ? labels
                : values;
            const model = await run(
              ant.set_enum(values, resolvedLabels, message),
            );
            return { model, values, labels: resolvedLabels, message };
          },
        },
      };
    },
  },
  manifest: {
    buildFieldMutations(fieldPath, spec) {
      return buildEnumFieldMutations(fieldPath, spec.enum);
    },
  },
  summary: {
    selections: {
      propertyFields: [`enum_rule { values labels message }`],
    },
    readPropertySummary(rawProperty) {
      const values = Array.isArray(rawProperty.enum_rule?.values)
        ? rawProperty.enum_rule.values.filter(
            (value: unknown): value is string =>
              typeof value === "string" && value.length > 0,
          )
        : [];
      if (values.length === 0) return { enumRule: null };
      const message =
        typeof rawProperty.enum_rule?.message === "string"
          ? rawProperty.enum_rule.message
          : null;
      const labels = Array.isArray(rawProperty.enum_rule?.labels)
        ? rawProperty.enum_rule.labels.filter(
            (label: unknown): label is string =>
              typeof label === "string" && label.length > 0,
          )
        : [];
      return {
        enumRule: {
          values,
          ...(labels.length === values.length ? { labels } : {}),
          ...(message ? { message } : {}),
        },
      };
    },
  },
  domain: {
    applyToPropertyIR(
      propertyIR: DomainPropertyIR,
      propertySummary: DomainPropertySummary,
    ) {
      const values = propertySummary.enumRule?.values || [];
      if (values.length === 0) return propertyIR;

      return {
        ...propertyIR,
        type: renderInlineStringUnion(values),
        docs: [
          ...propertyIR.docs,
          `Allowed values: ${values.join(", ")}.`,
          ...(propertySummary.enumRule?.message
            ? [propertySummary.enumRule.message]
            : []),
        ],
      };
    },
  },
});
